import type { CareDomain, IInsight, InsightConfidence, InsightType } from '../../insights/IInsight';
import {
    CONDITION_TOLERANCE,
    DEFAULT_WINDOW_WEEKS,
    LOW_CONFIDENCE_DISCOUNT,
    MIN_BASELINE_FINDINGS_FOR_STANDING,
    MIN_BASELINE_WEEKS_FOR_STANDING,
    MIN_FINDINGS_FOR_TREND,
    MIN_RECENT_FINDINGS_TO_FLAG,
    MIN_WEEKS_WITH_FINDINGS,
    RECENT_WEEKS,
    SEVERITY_BY_KIND
} from './agencyThresholds';
import { classifyFinding } from './agencyFindingKind';
import { domainsOf } from './agencyDomains';

/**
 * Turns a caseload's findings into the numbers the agency Insights tab draws.
 *
 * Kept free of React, Ionic and the API response shape on purpose: everything here is a pure
 * function over plain inputs, so the arithmetic that decides what a dispatcher sees first can be
 * tested directly rather than through the UI that renders it.
 *
 * Two readers are served from the same digest. A dispatcher needs to know who to see today, which
 * is a question about each client against their own recent normal. Agency leadership needs to know
 * whether the service is doing anything, which is a question about the caseload as a whole over
 * time. Both fall out of the same weekly buckets, so neither reader gets a number the other cannot
 * reconcile.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Three kinds, in worsening order.
 *
 * An earlier version split ordinary findings into "documentation" and "worth watching" and gave
 * documentation a purple of its own. It bought nothing a reader acts on: both are the same
 * instruction, which is to read the thing, and a fourth colour on a stacked bar cost more attention
 * than the distinction returned. They are one kind now.
 */
export type AgencyAlertKind = 'incident' | 'baseline' | 'observation';

/** One active finding, flattened from the caseload roll-up. */
export interface IAgencyFinding {
    careMapId: string;
    clientName: string;
    type: InsightType;
    confidence: InsightConfidence;
    title: string;
    narrative: string;
    /** The parts of daily life the api said this is about. Absent on findings from before it did. */
    domains?: CareDomain[];
    /** End of the evidence behind the finding, which is when it describes the client as being. */
    observedOn: string;
}

/**
 * An insight in the shape the scoring reads. `dateRangeTo` and not `dateRangeFrom`: the evidence
 * behind a finding ends when it stops describing the client, which is when it belongs on a
 * timeline. The agency tab and a client's Status page both map through here, so the same insight
 * scores the same way on each.
 */
export const toAgencyFinding = (params: { careMapId: string; clientName: string; insight: IInsight }): IAgencyFinding => {
    const { careMapId, clientName, insight } = params;
    return {
        careMapId,
        clientName,
        type: insight.type,
        confidence: insight.confidence,
        title: insight.title,
        narrative: insight.narrative,
        domains: insight.domains,
        observedOn: insight.dateRangeTo
    };
};

/**
 * What the digest needs to know about a client. Deliberately three booleans rather than the critical
 * info record: the coverage question is "is this on file", and keeping the schema out of here means a
 * new critical-info field cannot silently change what the chart counts.
 */
export interface IAgencyClient {
    careMapId: string;
    clientName: string;
    /**
     * False for a client whose record this organization cannot read. What is on file is then not
     * merely absent, it is unknown, and the two must not be counted together: an unreadable client
     * shown as a gap is a worklist item nobody can ever close.
     */
    coverageKnown: boolean;
    hasConfirmedCodeStatus: boolean;
    hasInsuranceOnFile: boolean;
    hasIdentityDocument: boolean;
    /**
     * When anything was last recorded on this client's care map. Absent when nothing has been, when
     * this organization cannot read the record privately, or when the api could not say.
     *
     * The only thing the tab has that tells a household which stopped writing from one whose record
     * is kept up and raised nothing, since no finding at all is the normal outcome of both.
     */
    lastRecordedAt?: string;
}

export interface IAgencyAlert {
    careMapId: string;
    clientName: string;
    kind: AgencyAlertKind;
    title: string;
    confidence: InsightConfidence;
    observedOn: string;
}

export interface IWeeklyPoint {
    /** Monday of the bucket, as YYYY-MM-DD. */
    weekStartsOn: string;
    incident: number;
    baseline: number;
    observation: number;
    total: number;
}

/**
 * Where a client sits against their own recent normal, not against the caseload's.
 *
 * 'unknown' is the absence of a reading rather than a fourth position on the scale: it is what the
 * tab reports for a client with too little earlier history to be compared against anything, or with
 * nothing recorded recently to compare with it. It sits in the union rather than being modelled as
 * an optional field so that every colour map, legend and filter is forced to name it, which is what
 * stops a surface quietly rendering "nothing is known here" as one of the other three.
 */
export type BaselineStanding = 'above' | 'steady' | 'settled' | 'unknown';

/**
 * One client against their own earlier weeks, as two separate quantities: how much is being
 * observed, and what the observations say. Only the second is compared. The counts decide only
 * whether there is enough evidence to state the comparison.
 */
export interface IClientBaseline {
    careMapId: string;
    clientName: string;
    /**
     * How much is being written down about this client.
     *
     * Findings a week moves with how much the household records as much as with how the client is:
     * reflection runs sooner the more is recorded, so a family that starts writing more produces more
     * findings about a client who has not changed. Ranking a caseload on it ranks engagement, so the
     * rates never set the standing; they stay for the strip's hover text.
     */
    volume: {
        baselinePerWeek: number;
        recentPerWeek: number;
        /**
         * Whether anything was recorded in the recent weeks, read off the care map's last write or
         * off any finding the weeks produced. False is a quiet record as far as the tab can tell, and
         * it withholds the reassuring standings rather than granting them.
         */
        recordedRecently: boolean;
    };
    /**
     * Whether the earlier weeks hold enough about this client to be a usual to compare against: the
     * evidence floor, before the direction is considered. The standing can be 'above' without it,
     * because the floors withhold only the reassuring readings, so anything claiming a client is back
     * within or below their own usual reads this rather than the standing.
     */
    hasUsual: boolean;
    /** What the findings about this client say, in the units of readCondition. */
    condition: {
        /** The earlier weeks, read in spans as long as the recent one and averaged. */
        usual: number;
        recent: number;
        change: number;
    };
    standing: BaselineStanding;
    /**
     * Whether the standing came from something that happened rather than from the condition.
     *
     * The standing answers "does anybody need to go and see this person", so an incident takes it
     * to 'above' whatever the rest of the record says. That makes it the wrong field for any surface
     * describing the CONDITION: a client who fell last week but has had less raised about them than
     * usual is above, and a strip that reads the standing draws them as worse across the board.
     * Surfaces about the condition read this and the change; surfaces about attention read the standing.
     */
    hasRecentIncident: boolean;
    /**
     * What the condition alone says, with the floors applied but before an incident overrides it.
     * Equal to `standing` for every client without a recent incident.
     *
     * Kept on the row because leaving every incident client out of the condition surfaces was itself
     * a claim that nothing else about them had moved, and nothing checked it. A fall usually brings
     * follow-up findings about the parts of life around it, so "fell" and "more raised than usual"
     * are the common case rather than alternatives.
     */
    conditionStanding: BaselineStanding;
}

export interface ICoverage {
    /** The denominator: clients whose record this organization can actually read. */
    clientCount: number;
    codeStatusConfirmed: number;
    insuranceOnFile: number;
    identityOnFile: number;
    /** Clients left out of the above because their record is not readable here. */
    unknownCount: number;
}

/** Which of the three things a caseload keeps on file a client is missing. */
export type CoverageKey = 'codeStatus' | 'insurance' | 'identity';

/**
 * What the reader has narrowed the tab to. Every surface is a way in: a tile, a legend swatch and a
 * week in the chart all set one of these, and every list below then answers the same question.
 * Holding it as one object rather than three independent toggles is what lets the surfaces stack, so
 * "incidents, in the week of the 3rd" is reachable without either control knowing about the other.
 *
 * Deliberately only dimensions of a FINDING and of a client's standing in it. What is on file for a
 * client is not one; the coverage meters answer "who is missing this" by listing them instead.
 */
export interface IInsightsFilter {
    kind?: AgencyAlertKind;
    standing?: BaselineStanding;
}

/**
 * Everything the Insights tab draws, from one pass over a caseload.
 *
 * Two invariants hold across every field, and the surfaces rely on both:
 *
 * Every finding-derived figure here counts only findings whose client is still in `clients`. A
 * finding outlives the client it was written about, so this is what stops a departed client
 * inflating the totals and putting a row in a list that opens nothing.
 *
 * That also makes the weekly chart a view of the CURRENT book over time rather than an immutable
 * record of what was served: history changes when a client leaves, and a caseload can drop back
 * below the volume needed to plot as a result. Internal consistency is worth more here than a stable
 * back-catalogue, since every surface is read side by side.
 */
export interface IAgencyDigest {
    windowWeeks: number;
    clientCount: number;
    /**
     * Clients the caseload has but this digest could not include, because they could not be read.
     *
     * Carried on the digest rather than handed to each surface separately so that a figure and the
     * caveat on it always come from the same place. Every "every client" and "nobody" sentence on
     * the tab is true only of what the digest holds, and without this they would be read as being
     * about the whole book.
     */
    unreadableCount: number;
    findingCount: number;
    alerts: IAgencyAlert[];
    weekly: IWeeklyPoint[];
    baselines: IClientBaseline[];
    coverage: ICoverage;
    /** Carried so a coverage meter can name the clients behind it, not just count them. */
    clients: IAgencyClient[];
    /**
     * The findings from the recent part of the window, whole rather than flattened into alerts.
     * The per-client hexagon shows their words under each part of daily life, and an alert has
     * already dropped the narrative by the time it reaches the surface.
     */
    recentFindings: IAgencyFinding[];
    /**
     * Every finding in the whole window, whole rather than flattened.
     *
     * `recentFindings` starts on a bucket boundary, so on a Monday it reaches back only 21 days.
     * Anything reading a rolling last-28-days span needs a set that always covers it, or the oldest
     * span is silently empty and a client's decline disappears for the first days of every week.
     */
    windowFindings: IAgencyFinding[];
    /** Monday of the first recent bucket, as YYYY-MM-DD, so a caller can say what "recent" covered. */
    recentStartsOn: string;
    /**
     * The moment the digest was built for, as an ISO timestamp.
     *
     * Carried because anything reading a rolling window needs the same "now" the buckets were cut
     * with. Deriving it from the last bucket instead lands a week in the future, and a last-seven-days
     * window anchored there quietly excludes everything that actually happened.
     */
    readAt: string;
    /** Findings per week across the caseload, older part of the window against the recent part. */
    attentionTrend: { earlierPerWeek: number; recentPerWeek: number };
}

/** Midnight UTC on the Monday of the week containing `date`. Bucketing in UTC keeps a
 *  daylight-saving change from moving a finding into the neighbouring week. */
export const startOfUtcWeek = (date: Date): Date => {
    const midnight = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    // getUTCDay is 0 on Sunday, which belongs to the week that began six days earlier.
    const dayOfWeek = new Date(midnight).getUTCDay();
    const daysSinceMonday = (dayOfWeek + 6) % 7;
    return new Date(midnight - daysSinceMonday * DAY_MS);
};

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

const emptyWeek = (weekStartsOn: string): IWeeklyPoint => ({
    weekStartsOn,
    incident: 0,
    baseline: 0,
    observation: 0,
    total: 0
});

const perWeek = (count: number, weeks: number): number => {
    if (weeks <= 0) {
        return 0;
    }
    // One decimal place: these are rates on a small denominator and any more precision reads as
    // measurement the data cannot support.
    return Math.round((count / weeks) * 10) / 10;
};

const severityOf = (finding: IAgencyFinding): number => {
    const weight = SEVERITY_BY_KIND[classifyFinding(finding)];
    return finding.confidence === 'low' ? weight - LOW_CONFIDENCE_DISCOUNT : weight;
};

/**
 * What a set of findings says about how the client is: for each part of daily life, the most
 * serious thing raised about it, added up across the six.
 *
 * This is the quantity the standing compares, and it is built not to count how much was written. A
 * part of life counts once however many findings raise it, so a household recording twice as much
 * about the same things reads the same. The same number of findings spreading into new parts of
 * life, or turning from drifts into things that stood out, reads higher. A finding about no part of
 * life is about the arrangements around the client (a booking, a refill, the household's own
 * logging) and counts for nothing here.
 *
 * The limit, stated plainly: nothing on a finding says whether things got worse or better, so how
 * serious it is gets read off its kind and confidence. A drift the model framed as good news still
 * counts as something raised unless it was low confidence, and a long-standing problem in one part of
 * life reads the same whether it is easing or not. Nor does it ignore volume outright. It saturates:
 * a household writing a great deal more covers more ground and so touches more parts of life, and a
 * big enough jump in recording can still nudge the reading up. The same is true in reverse, which is
 * the one that matters: a household writing much less, but not nothing, covers less ground and can
 * read as settled. Only a count of what was recorded could tell that apart, and the tab has none.
 */
export const readCondition = (findings: IAgencyFinding[]): number => {
    const worstByDomain = new Map<CareDomain, number>();
    for (const finding of findings) {
        const severity = severityOf(finding);
        for (const domain of domainsOf(finding)) {
            worstByDomain.set(domain, Math.max(worstByDomain.get(domain) ?? 0, severity));
        }
    }
    return [...worstByDomain.values()].reduce((total, one) => total + one, 0);
};

/**
 * Where one client's CONDITION sits against their own earlier weeks, which is three of the four
 * rules. The fourth, an incident, is applied by the caller, and both readings are kept on the row
 * because the surfaces need different ones: a strip of bars about what the findings say needs this
 * one, and a colour about whether to visit needs the standing.
 *
 * An incident outranks the condition. The standing is a comparison against this client's own earlier
 * weeks, so somebody who had a lot raised for months and then had one fall comes out with less raised
 * than usual and would otherwise read as settled: a green cell with a red incident badge on it, a
 * green bar on the strip, and an absence from the list of clients worth looking at. Settled has to
 * mean nothing needs a visit, and a fall does. The same is true of steady, and of a client too new to
 * have a reading at all, which is why this is the first thing asked and not a promotion applied to one
 * value. Only an incident in the recent span counts, because letting an old one hold somebody at
 * declining indefinitely is how a worklist stops being read. Note what that span actually is: the
 * last three to four whole week-buckets, so an incident stops counting somewhere between 21 and 28
 * days old, and always at a Monday boundary rather than on its own anniversary. A client can go
 * from a coral cell to a green one overnight with no new data, which is the first thing to check
 * if somebody reports a client turning green on their own. The second thing is the earlier spans,
 * which move a day at a time (see earlierSpans).
 *
 * Then the condition, with two floors over the top of it.
 *
 * Nothing recorded in the recent weeks and there is nothing to read, so neither steady nor settled
 * can be claimed. This is a quiet record, which must never read as a client getting better.
 * It does not read as needing a visit either: silence is not evidence of a decline.
 *
 * Too little earlier history and there is no normal to compare against, so the reassuring readings
 * are withheld: nothing raised then and nothing now is a change of zero, which lands mid-tolerance
 * and would render the least-known client as the most confident cell on the map.
 *
 * The floors gate 'steady' and 'settled' only, never 'above'. Saying somebody is FINE is the claim
 * that needs evidence behind it; saying something is happening needs less, because the two mistakes
 * do not cost the same. A client wrongly flagged costs a visit that was not needed. A client
 * wrongly shown as settled is one nobody goes to see. That asymmetry is already how a rise itself is
 * read, in MIN_RECENT_FINDINGS_TO_FLAG, and the floors follow it.
 */
const conditionStandingForClient = (params: {
    change: number;
    recentAboutThem: number;
    recordedRecently: boolean;
    hasUsual: boolean;
}): BaselineStanding => {
    const { change, recentAboutThem, recordedRecently, hasUsual } = params;

    if (change > CONDITION_TOLERANCE && recentAboutThem >= MIN_RECENT_FINDINGS_TO_FLAG) {
        return 'above';
    }

    if (!recordedRecently || !hasUsual) {
        return 'unknown';
    }

    return change < -CONDITION_TOLERANCE ? 'settled' : 'steady';
};

const buildCoverage = (clients: IAgencyClient[]): ICoverage => {
    const known = clients.filter(client => client.coverageKnown);

    return {
        clientCount: known.length,
        codeStatusConfirmed: known.filter(client => client.hasConfirmedCodeStatus).length,
        insuranceOnFile: known.filter(client => client.hasInsuranceOnFile).length,
        identityOnFile: known.filter(client => client.hasIdentityDocument).length,
        unknownCount: clients.length - known.length
    };
};

/**
 * The earlier weeks cut into spans exactly as long as the recent span has run, newest first, so
 * the comparison is like for like. A reading counts the parts of life something was raised about,
 * so a longer span has longer to raise something in. Read whole, eight weeks would make every
 * client look better recently than they used to; read in fixed four-week spans against a recent
 * span the current week leaves three to four weeks long, every Monday would lean toward settled.
 *
 * Whatever is left over at the old end, up to two weeks on a Monday, is not read, and does not count
 * toward the evidence floor either: a normal the reading cannot see is not one it can compare with.
 *
 * Whole days only, so the spans move once a day rather than with every hour the tab stays open.
 */
const earlierSpans = (params: {
    windowStart: Date;
    recentStart: Date;
    now: Date;
}): { spans: { from: number; to: number }[]; readFrom: number } => {
    const { windowStart, recentStart, now } = params;

    const recentLength = Math.floor((now.getTime() - recentStart.getTime()) / DAY_MS) * DAY_MS;
    const spanCount = recentLength > 0
        ? Math.max(0, Math.floor((recentStart.getTime() - windowStart.getTime()) / recentLength))
        : 0;

    const spans = Array.from({ length: spanCount }, (unused, index) => {
        const to = recentStart.getTime() - index * recentLength;
        return { from: to - recentLength, to };
    });
    return { spans, readFrom: recentStart.getTime() - spanCount * recentLength };
};

const usualCondition = (params: { clientFindings: IAgencyFinding[]; spans: { from: number; to: number }[] }): number => {
    const { clientFindings, spans } = params;
    if (spans.length === 0) {
        return 0;
    }

    const readings = spans.map(span => readCondition(clientFindings.filter(finding => {
        const at = new Date(finding.observedOn).getTime();
        return at >= span.from && at < span.to;
    })));
    return readings.reduce((total, one) => total + one, 0) / spans.length;
};

/**
 * How many distinct weeks a set of findings falls in.
 *
 * Bucketed on the same week grid the chart draws, so "two weeks" means the same thing here as it
 * does everywhere else on the tab rather than meaning any two dates fourteen days apart.
 */
const distinctWeeks = (findings: IAgencyFinding[]): number => {
    return new Set(findings.map(finding => startOfUtcWeek(new Date(finding.observedOn)).getTime())).size;
};

const recordedSince = (client: IAgencyClient, since: Date): boolean => {
    return client.lastRecordedAt !== undefined && new Date(client.lastRecordedAt).getTime() >= since.getTime();
};

/**
 * Every client the caseload holds gets a baseline row, including those with nothing outstanding.
 * A client the tab cannot yet say anything about needs to be visibly present and visibly
 * unmeasured, rather than absent and indistinguishable from one who was never loaded.
 */
const buildBaselines = (params: {
    findings: IAgencyFinding[];
    clients: IAgencyClient[];
    windowStart: Date;
    recentStart: Date;
    now: Date;
}): IClientBaseline[] => {
    const { findings, clients, windowStart, recentStart, now } = params;

    const baselineWeeks = (recentStart.getTime() - windowStart.getTime()) / WEEK_MS;
    const recentWeeks = (now.getTime() - recentStart.getTime()) / WEEK_MS;

    const observedAt = (finding: IAgencyFinding) => new Date(finding.observedOn).getTime();
    const isBaseline = (finding: IAgencyFinding) => {
        const at = observedAt(finding);
        return at >= windowStart.getTime() && at < recentStart.getTime();
    };
    const isRecent = (finding: IAgencyFinding) => observedAt(finding) >= recentStart.getTime();
    const isAboutThem = (finding: IAgencyFinding) => domainsOf(finding).length > 0;

    const { spans, readFrom } = earlierSpans({ windowStart, recentStart, now });
    const isRead = (finding: IAgencyFinding) => observedAt(finding) >= readFrom && observedAt(finding) < recentStart.getTime();

    return clients.map(client => {
        const mine = findings.filter(finding => finding.careMapId === client.careMapId);
        const recent = mine.filter(isRecent);
        const inBaseline = mine.filter(isBaseline).length;
        // The floor asks whether there is a normal for this PERSON to compare against, so a run of
        // bookings and refills is not one: it would read nothing against nothing as steady.
        const baselineAboutThem = mine.filter(isRead).filter(isAboutThem);

        const usual = usualCondition({ clientFindings: mine, spans });
        const recentReading = readCondition(recent);
        // Decided on the unrounded change and only the display values are rounded, so an average of
        // two spans cannot be rounded across the tolerance.
        const change = recentReading - usual;

        // A finding in the recent weeks means somebody recorded something then, whether or not the
        // api could say when the care map was last written to.
        const recordedRecently = recent.length > 0 || recordedSince(client, recentStart);

        const hasUsual = baselineAboutThem.length >= MIN_BASELINE_FINDINGS_FOR_STANDING
            && distinctWeeks(baselineAboutThem) >= MIN_BASELINE_WEEKS_FOR_STANDING;

        const hasRecentIncident = recent.some(finding => classifyFinding(finding) === 'incident');
        const conditionStanding = conditionStandingForClient({
            change,
            recentAboutThem: recent.filter(isAboutThem).length,
            recordedRecently,
            hasUsual
        });

        return {
            careMapId: client.careMapId,
            clientName: client.clientName,
            volume: {
                baselinePerWeek: perWeek(inBaseline, baselineWeeks),
                recentPerWeek: perWeek(recent.length, recentWeeks),
                recordedRecently
            },
            hasUsual,
            condition: {
                usual: Math.round(usual * 10) / 10,
                recent: recentReading,
                change: Math.round(change * 10) / 10
            },
            hasRecentIncident,
            conditionStanding,
            standing: hasRecentIncident ? 'above' : conditionStanding
        };
    });
};

/**
 * The findings a digest may count: this window, and a client still on the book.
 *
 * A finding outlives the client it was written about, so a client who has been removed or
 * transferred away leaves theirs behind. Dropping them once, here, is what keeps the tab
 * consistent: every total, chart and list is derived from this one set, and the detail view is
 * built from the roster, so a finding with nobody behind it would both inflate the counts and put
 * a row in Recent Changes that opens nothing when it is clicked.
 */
const findingsInWindow = (params: {
    findings: IAgencyFinding[];
    clients: IAgencyClient[];
    windowStart: Date;
    now: Date;
}): IAgencyFinding[] => {
    const { findings, clients, windowStart, now } = params;
    const onBook = new Set(clients.map(client => client.careMapId));

    return findings.filter(finding => {
        if (!onBook.has(finding.careMapId)) {
            return false;
        }
        const at = new Date(finding.observedOn).getTime();
        // A finding dated in the future is a clock or data error rather than something to plot, and
        // it would stretch the axis past today and flatten everything real.
        return !isNaN(at) && at >= windowStart.getTime() && at <= now.getTime();
    });
};

/**
 * The chart's week buckets and the flat alert list, built in one pass.
 *
 * They are produced together because they are two readings of the same findings, and computing
 * them apart is how a chart and the list under it come to disagree about a week.
 */
const bucketFindings = (params: {
    inWindow: IAgencyFinding[];
    firstWeekStart: Date;
    windowWeeks: number;
}): { weekly: IWeeklyPoint[]; alerts: IAgencyAlert[] } => {
    const { inWindow, firstWeekStart, windowWeeks } = params;

    const weekly: IWeeklyPoint[] = [];
    const byWeek = new Map<string, IWeeklyPoint>();
    for (let index = 0; index < windowWeeks; index++) {
        const point = emptyWeek(toIsoDate(new Date(firstWeekStart.getTime() + index * WEEK_MS)));
        weekly.push(point);
        byWeek.set(point.weekStartsOn, point);
    }

    const alerts: IAgencyAlert[] = [];
    for (const finding of inWindow) {
        const kind = classifyFinding(finding);
        const bucket = byWeek.get(toIsoDate(startOfUtcWeek(new Date(finding.observedOn))));
        if (bucket) {
            bucket[kind]++;
            bucket.total++;
        }
        alerts.push({
            careMapId: finding.careMapId,
            clientName: finding.clientName,
            kind,
            title: finding.title,
            confidence: finding.confidence,
            observedOn: finding.observedOn
        });
    }

    return { weekly, alerts };
};

export const buildAgencyDigest = (params: {
    findings: IAgencyFinding[];
    clients: IAgencyClient[];
    now: Date;
    windowWeeks?: number;
    /** Clients left out of `clients` because they could not be read. */
    unreadableCount?: number;
}): IAgencyDigest => {
    const { findings, clients, now } = params;
    const unreadableCount = params.unreadableCount ?? 0;
    const windowWeeks = params.windowWeeks ?? DEFAULT_WINDOW_WEEKS;

    const thisWeekStart = startOfUtcWeek(now);
    const firstWeekStart = new Date(thisWeekStart.getTime() - (windowWeeks - 1) * WEEK_MS);
    const windowStart = firstWeekStart;

    // The baseline/recent split lands on the same week grid the chart is bucketed on, so the
    // baseline is exactly the first (windowWeeks - RECENT_WEEKS) buckets. Splitting on a raw
    // "now minus four weeks" instead would put the boundary mid-bucket and leave the baseline
    // spanning 7 to 8 weeks depending on the weekday it happened to be read on, which both
    // contradicts the eight weeks this is documented and labelled as and inflates every client's
    // baseline rate.
    const recentStart = new Date(thisWeekStart.getTime() - (RECENT_WEEKS - 1) * WEEK_MS);

    const inWindow = findingsInWindow({ findings, clients, windowStart, now });
    const { weekly, alerts } = bucketFindings({ inWindow, firstWeekStart, windowWeeks });

    const earlier = inWindow.filter(finding => new Date(finding.observedOn).getTime() < recentStart.getTime()).length;
    const recent = inWindow.length - earlier;
    const earlierWeeks = (recentStart.getTime() - windowStart.getTime()) / WEEK_MS;
    const recentWeeksElapsed = (now.getTime() - recentStart.getTime()) / WEEK_MS;

    return {
        unreadableCount,
        windowWeeks,
        clientCount: clients.length,
        findingCount: inWindow.length,
        alerts,
        weekly,
        baselines: buildBaselines({ findings: inWindow, clients, windowStart, recentStart, now }),
        coverage: buildCoverage(clients),
        clients,
        recentFindings: inWindow.filter(finding => new Date(finding.observedOn).getTime() >= recentStart.getTime()),
        windowFindings: inWindow,
        recentStartsOn: toIsoDate(recentStart),
        readAt: now.toISOString(),
        attentionTrend: {
            earlierPerWeek: perWeek(earlier, earlierWeeks),
            recentPerWeek: perWeek(recent, recentWeeksElapsed)
        }
    };
};

/**
 * Whether a client belongs on the deviation strip, which draws movement in what the findings say.
 *
 * Exported so the bars and the sentence under them read one predicate instead of two copies that
 * drift. Membership is decided on the CONDITION, not the standing: a client flagged for a fall is on
 * the map in coral and in the attention list either way, and whether they also belong here is a
 * separate question about everything else raised about them.
 *
 * A condition that eased under a recent incident is the one case left out. Including it would draw
 * a green bar pointing at Settling for somebody the map has just coloured coral, which is the
 * contradiction every other surface is arranged to avoid.
 */
export const movedTheirCondition = (baseline: IClientBaseline): boolean => {
    if (baseline.conditionStanding === 'above') {
        return true;
    }
    return baseline.conditionStanding === 'settled' && !baseline.hasRecentIncident;
};

/**
 * Clients absent from every coverage meter, by either route that puts them there.
 *
 * `unreadableCount` is what the fan-out classified. `unknownCount` catches a client that reached
 * the digest with no coverage anyway, which happens whenever the care map list has moved on since
 * that fan-out ran. The two sets are disjoint: `clients` excludes every id the fan-out named, and
 * `unknownCount` counts only what is left. Derived here so the meters and the sentence under them
 * cannot drift apart, which is how the single-count version of this went wrong.
 */
export const notCountedInCoverage = (digest: IAgencyDigest): number => {
    return digest.unreadableCount + digest.coverage.unknownCount;
};

/**
 * Whether the organization has clients but could read none of them.
 *
 * Distinct from an empty book, and the two cannot be told apart from `clientCount` alone. The tab
 * draws none of its figures in this state: every figure would be a ratio over nobody.
 */
export const hasNothingReadable = (digest: IAgencyDigest): boolean => {
    return digest.clientCount === 0 && digest.unreadableCount > 0;
};

const weeksWithFindings = (digest: IAgencyDigest): number => digest.weekly.filter(point => point.total > 0).length;

/** Whether the caseload has enough history for the panels that compare across weeks. */
export const hasEnoughHistory = (digest: IAgencyDigest): boolean => {
    return digest.findingCount >= MIN_FINDINGS_FOR_TREND && weeksWithFindings(digest) >= MIN_WEEKS_WITH_FINDINGS;
};

export interface IHistoryProgress {
    hasEnough: boolean;
    findingCount: number;
    findingsNeeded: number;
    weeksWithFindings: number;
    weeksNeeded: number;
    /** The window the counts cover: findings older than this have aged out and no longer count. */
    windowWeeks: number;
    /** Whole weeks after this one before the caseload could have enough. Absent once it has. */
    weeksToWait?: number;
    /**
     * Monday of the first week the caseload could have enough, as YYYY-MM-DD. Absent once it has.
     * The earliest rather than a forecast: it assumes recording carries on, and an old finding
     * ageing out of the window can push it later.
     */
    earliestWeekStartsOn?: string;
}

/** How far the caseload is from the history its comparisons need, for the panels waiting on it. */
export const historyProgress = (digest: IAgencyDigest): IHistoryProgress => {
    const progress: IHistoryProgress = {
        hasEnough: hasEnoughHistory(digest),
        findingCount: digest.findingCount,
        findingsNeeded: MIN_FINDINGS_FOR_TREND,
        weeksWithFindings: weeksWithFindings(digest),
        weeksNeeded: MIN_WEEKS_WITH_FINDINGS,
        windowWeeks: digest.windowWeeks
    };
    if (progress.hasEnough) {
        return progress;
    }

    // The current week can still become one of the weeks needed if nothing has landed in it yet.
    const thisWeek = digest.weekly[digest.weekly.length - 1];
    const weeksShort = Math.max(0, MIN_WEEKS_WITH_FINDINGS - progress.weeksWithFindings);
    const weeksToWait = weeksShort === 0 ? 0 : weeksShort - (thisWeek.total > 0 ? 0 : 1);
    const earliest = new Date(Date.parse(`${thisWeek.weekStartsOn}T00:00:00Z`) + weeksToWait * WEEK_MS);

    return { ...progress, weeksToWait, earliestWeekStartsOn: toIsoDate(earliest) };
};

export const isFiltered = (filter: IInsightsFilter): boolean => {
    return Boolean(filter.kind || filter.standing);
};

export const clientsMissing = (digest: IAgencyDigest, key: CoverageKey): IAgencyClient[] => {
    return digest.clients.filter(client => {
        // A client whose record cannot be read is not missing anything as far as this reader knows.
        if (!client.coverageKnown) {
            return false;
        }
        if (key === 'codeStatus') {
            return !client.hasConfirmedCodeStatus;
        }
        if (key === 'insurance') {
            return !client.hasInsuranceOnFile;
        }
        return !client.hasIdentityDocument;
    });
};

const clientIdsWithStanding = (digest: IAgencyDigest, filter: IInsightsFilter): Set<string> | undefined => {
    if (!filter.standing) {
        return undefined;
    }
    return new Set(
        digest.baselines
            .filter(baseline => baseline.standing === filter.standing)
            .map(baseline => baseline.careMapId)
    );
};

export const filterAlerts = (digest: IAgencyDigest, filter: IInsightsFilter): IAgencyAlert[] => {
    const standing = clientIdsWithStanding(digest, filter);

    return digest.alerts.filter(alert => {
        if (filter.kind && alert.kind !== filter.kind) {
            return false;
        }
        if (standing && !standing.has(alert.careMapId)) {
            return false;
        }
        return true;
    });
};

/**
 * A kind describes a finding rather than a client, so it reaches the client list only through the
 * findings: filtering to "incidents" leaves the clients who have one, which is what a reader
 * clicking the incident tile is asking to see.
 */
export const filterBaselines = (digest: IAgencyDigest, filter: IInsightsFilter): IClientBaseline[] => {
    const viaFindings = filter.kind
        ? new Set(filterAlerts(digest, { kind: filter.kind }).map(alert => alert.careMapId))
        : undefined;

    return digest.baselines.filter(baseline => {
        if (filter.standing && baseline.standing !== filter.standing) {
            return false;
        }
        if (viaFindings && !viaFindings.has(baseline.careMapId)) {
            return false;
        }
        return true;
    });
};
